/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/


import { CommonModule } from "@angular/common";
import {
	Component,
	EventEmitter,
	Output,
	forwardRef,
	input
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ChipComponent } from "../chip/chip.component";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

export interface ButtonGroupOption {
	icon?: string;
	label?: string;
	count?: number;
	value: unknown;
}

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => ButtonGroupComponent),
	multi: true,
};

@Component({
	selector: "cb-button-group",
	standalone: true,
	imports: [CommonModule, IconComponent, TranslateModule, ChipComponent, TooltipModule],
	templateUrl: "./button-group.component.html",
	styleUrl: "./button-group.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class ButtonGroupComponent implements ControlValueAccessor {
	readonly options = input.required<ButtonGroupOption[]>();
	public readonly label = input<string>();
	public readonly info = input<string>();
	public readonly nullable = input(false);
	public readonly disabled = input(false);
	public readonly fullWidth = input(false);
	public readonly required = input(false);
	public readonly error = input(false);
	public readonly translateOptions = input(true);

	@Output() public change: EventEmitter<unknown | unknown[]> = new EventEmitter<
		unknown | unknown[]
	>();

	private innerValue!: unknown | unknown[];

	/**
	 * Triggers when the user clicks on the button.
	 * set the value based on the type
	 * if it's a number it can only be one option, if it's an array it cuold be multiple
	 * check if nullable it's false the value can't be null
	 */

	public setValue(v: unknown) {
		if (!Array.isArray(this.value)) {
			const nullable = this.nullable();
			if (!nullable && v === this.value) return;
			if (nullable && v === this.value) {
				this.value = null;
				return;
			}
			this.value = v;
			return;
		}
		if (Array.isArray(this.value)) {
			if (!this.value.includes(v)) {
				this.value.push(v);
			} else if (this.value.includes(v)) {
				if (!this.nullable() && this.value.length === 1) return;
				const index = this.value.findIndex((el) => el === v);
				if (index !== -1) {
					this.value.splice(index, 1);
				}
			}
			this.onChangeCallback(this.value);
			this.change.emit(this.value);
		}
	}

	public checkIsSelected(i: unknown): boolean {
		if (!Array.isArray(this.value)) {
			if (this.value === i) {
				return true;
			}
			return false;
		}
		if (Array.isArray(this.value)) {
			return this.value.includes(i);
		}
		return false;
	}

	//The internal data model

	public get value(): unknown | unknown[] {
		return this.innerValue;
	}

	public set value(v: unknown | unknown[]) {
		if (v !== this.innerValue) {
			this.innerValue = v;
			this.onChangeCallback(v);
			this.change.emit(v);
		}
	}

	public writeValue(obj: unknown | unknown[]): void {
		if (obj !== this.innerValue) {
			this.innerValue = obj;
		}
	}
	private onTouchedCallback: () => void = noop;
	private onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}
}
